export enum SecureMessageListType {
  Sent = 'S',
  Received = 'R'
}

export enum SecureMessageListStatus {
  Active = 'active',
  Archived = 'archived'
}

/** `is_archive` on get-sec-messages: 0 returns active rows, 1 returns archived ones. */
export type SecureMessageArchiveFlag = 0 | 1;

export interface GetAllSecureMessagesParams {
  limit?: number;
  sentSkip?: number;
  receivedSkip?: number;
  status?: SecureMessageListStatus;
  includeSent?: boolean;
  includeReceived?: boolean;
}

export interface SecureMessageListItem {
  /** Thread identifier — one list row per thread in get-sec-messages. */
  msg_id: number;
  /** Same as msg_id when returned by the API; used when merging sent/received lists. */
  thread_id?: number;
  snd_type_id: number;
  snd_id: number;
  snd_name: string;
  snd_date: string;
  snd_time: string;
  subject: string;
  /** Present on some API responses instead of `subject`. */
  msg_subject?: string;
  /** Message body when `subject` is empty (common on sent list). */
  text?: string;
  msg_text?: string;
  priority: number;
  is_read: number;
  is_archive: number;
  is_starred: number;
  is_system_msg?: number | boolean;
  /**
   * Client-side only: every row id merged into this thread (sent + received).
   * `is_archive` lives on the row, not the thread, so archiving has to cover all of them.
   */
  msgIds?: number[];
}

export interface GetSecureMessagesQueryResult {
  list: SecureMessageListItem[];
  hasMoreData: boolean;
  sentHasMore?: boolean;
  receivedHasMore?: boolean;
}

export interface GetSecureMessagesResponse {
  /** Boolean on current API builds, numeric on older ones. */
  has_more_data: boolean | number;
  list: SecureMessageListItem[];
}

export interface GetSecureMessagesParams {
  limit?: number;
  skip?: number;
  list_type?: SecureMessageListType;
  thread_id?: number;
  is_archive?: SecureMessageArchiveFlag;
}

export interface SecureMessageConversationItem {
  msg_id: number;
  sender_type: string;
  sender_id: number;
  sender_name: string;
  sender_descr: string;
  sender_photo_url: string;
  recipient_type: string;
  recipient_id: number;
  recipient_name: string;
  recipient_photo_url: string;
  msg_subject: string;
  msg_text: string;
  msg_ts: string;
  msg_direction: 'in' | 'out';
  is_system_msg: boolean | number;
  message_closed: boolean;
}

export interface ReplySecureMessageParams {
  msg_id: number;
  msg_text: string;
}

export interface ReplySecureMessageResponse {
  success: boolean | number;
  msg_id: number;
}

export interface MarkSecureMessageUnreadParams {
  msg_id: number;
}

export interface MarkSecureMessageUnreadResponse {
  success: boolean | number;
  msg_id?: number;
}

export interface ArchiveSecureMessageParams {
  msg_id: number;
  /** `1` restores a previously archived message instead of archiving it. */
  revert?: 1;
}

export interface ArchiveSecureMessageResponse {
  success: boolean | number;
}

/** UI-level archive toggle for a whole thread — see `SecureMessageListItem.msgIds`. */
export interface SetSecureMessageArchivedParams {
  msgIds: number[];
  isArchived: boolean;
}
